import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  FormField,
  Input,
  Button,
  Select,
  StatusIndicator,
  Alert,
  Badge,
  ColumnLayout,
  ExpandableSection,
} from '@cloudscape-design/components';
import { useAuth } from '../hooks/useAuth';
import { formatNeedsReviewReason } from '../utils/needsReviewReason';
import { parseKeyFinding } from '../utils/keyFinding';

// ============================================================================
// PROGRAM VALIDATION PAGE — Alpha-only
//
// This page is gated by environment check in App.js (getEnvironment() === 'alpha').
// It does NOT render in gamma or prod.
//
// Flow:
//   1. Dropdown populated from GET /programs API (filtered by user permissions).
//   2. User selects program + enters FR ID (or link from prod APFP portal).
//   3. Click "Validate FR" → popup opens APFP portal; the Chronos Token
//      Capture userscript (hosted at /chronos-token-capture.user.js on this
//      same bucket) reads the APFP id_token and sends it back via postMessage.
//   4. POST /validate with the token in the body. invoke_validation invokes
//      the AgentCore agent directly with the token in the payload (no
//      server-side storage of the token).
//   5. Polls GET /review-status/{id} showing state transitions
//      (1-Pending → 2-In Progress → 3-Completed / 4-Failed).
//   6. On completion, fetches GET /validations/{id} for the full result.
//   7. Export generates ZIP with: decision.json, summary.md, report.html.
//
// postMessage protocol (handshake with the userscript):
//   - `chronos_apfp_ready` — sent by the script on document-start. Lets us
//      tell "userscript not installed" (no ping in 5s) apart from "userscript
//      installed but the user is not logged into APFP" (ping arrives, token
//      never does). Both used to manifest as the same opaque 30s timeout.
//   - `chronos_apfp_token` — the captured Authorization header value.
//   The script posts only to `https://alpha.chronos.aws.dev`, and that
//   targetOrigin is what keeps the token from any other page that opens APFP
//   in a popup. The listener-side check
//   (`event.origin === 'https://funding.apn.a2z.com'`) only rejects forged
//   messages. See public/chronos-token-capture.user.js for the script source.
// ============================================================================

// API base URL — comes from runtime config (window.APP_CONFIG.API_BASE_URL)
// Set at deploy time by the CDK pipeline per environment.
// In local dev: http://localhost:3001 (mock_api_gateway.py)
const VALIDATION_API = window.APP_CONFIG?.API_BASE_URL || '';
if (!VALIDATION_API) {
  console.warn('[ValidationTest] APP_CONFIG.API_BASE_URL missing — defaulting to empty. Alpha features may not work.');
}

const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

// Make an arbitrary string safe to drop into a single Markdown TABLE cell:
// escape pipes (which would start a new column) and collapse newlines (which
// would break the row). Agent-supplied needs_review_detail is free text and
// can contain both (batpur review).
const escapeMarkdownCell = (s) =>
  String(s ?? '')
    .replace(/\|/g, '\\|')
    .replace(/\r?\n/g, ' ')
    .trim();

// Extract FR ID from full APFP URL or raw ID
const extractFrId = (input) => {
  if (!input) return '';
  // Handle full URL: https://funding.apn.a2z.com/fund-requests/FR-MAP-CASH-...
  const urlMatch = input.match(/fund-requests\/([^/?#]+)/);
  if (urlMatch) return urlMatch[1];
  // Handle raw ID
  return input.trim();
};

export const buildValidationRequest = (fundRequestId, requestedProgram, apfpToken) => ({
  fundRequestId,
  requestedProgram,
  apfp_token: apfpToken,
});

export const getEffectiveProgram = (reviewStatus = {}, requestedProgram = '') => (
  reviewStatus.routing_program_hint
  || reviewStatus.program
  || requestedProgram
  || '-'
);

const POLL_INTERVAL = 3000;
const POLL_TIMEOUT = 420000; // 7 minutes — MAP/WMP runs take 4-5 min

const getDecisionColor = (decision) => {
  const d = decision?.toLowerCase() || '';
  if (d === 'recommend_approval') return 'green';
  if (d === 'recommend_rejection') return 'red';
  if (d === 'needs_review') return 'blue';
  if (d.includes('approve')) return 'green';
  if (d.includes('reject')) return 'red';
  return 'blue';
};

// Derive a debugger verdict from the fetched trace events. Pure, defensive.
// Surfaces the common silent-fail case the page used to mask: the agent
// finished its invocation but never wrote a terminal review_state for this
// FR (e.g. soft tool failure before make_final_decision), so the page sat
// at "Waiting for agent to complete..." for the full POLL_TIMEOUT before
// surfacing a generic "Timed out" with no clue why.
const summarizeTrace = (events = [], reviewState = '') => {
  // The default parameter only applies when `events` is `undefined`, not
  // `null`. Call sites pass `trace.events` directly, and other code in
  // this file uses `trace.events || []` guards — implicit confirmation
  // that the field can land as `null`. Normalize once before any array
  // method touches it.
  const evts = events ?? [];
  const errs = evts.filter(e =>
    ['ERROR', 'CRITICAL', 'FATAL'].includes(String(e.level || '').toUpperCase())
    || e.error_type || e.error_message
  );
  const agentCompleted = evts.some(e => /invocation completed/i.test(e.message || ''));
  const noTerminal = reviewState === '2-In Progress' || reviewState === '1-Pending' || !reviewState;
  let verdict = null, verdictType = null;
  if (noTerminal && agentCompleted) {
    verdictType = 'error';
    // "See the errors below" only makes sense when the Errors sub-block
    // will actually render (gated on errs.length below). When the agent
    // skipped the terminal-write step without emitting any ERROR-level
    // events (a silent-logic-bug path), the original wording pointed at
    // an empty section. Branch on errs.length so the directive matches
    // what the user will see.
    verdict = errs.length
      ? 'Agent finished but never wrote a terminal decision for this fund request — this is why the run timed out. The agent most likely failed before its terminal-write step. See the errors below.'
      : 'Agent finished but never wrote a terminal decision for this fund request — this is why the run timed out. No explicit errors were logged; the agent may have silently skipped its terminal-write step.';
  } else if (noTerminal && !agentCompleted) {
    verdictType = 'warning';
    verdict = 'No agent-completion marker found in the trace window — the agent may still be running, have crashed, or never started.';
  }
  return { errs, agentCompleted, verdict, verdictType };
};

// Map an agent log level to a Cloudscape StatusIndicator type for the trace.
const traceLevelToType = (level) => {
  const l = String(level || '').toUpperCase();
  if (l === 'ERROR' || l === 'CRITICAL' || l === 'FATAL') return 'error';
  if (l === 'WARNING' || l === 'WARN') return 'warning';
  return 'info';
};

// Epoch millis → HH:MM:SS UTC for trace rows.
const fmtClock = (ms) => {
  if (!ms) return '';
  try { return new Date(ms).toISOString().slice(11, 19); } catch { return ''; }
};

// Metadata written into trace_diagnosis.json by handleExportTrace. The review
// row is authoritative for the runtime selected by Program Router; the
// validated-fundrequest row is authoritative for the persisted business
// verdict. Keeping both separate from the locally inferred diagnosis avoids
// presenting a trace heuristic as the agent's stored decision.
export const buildTraceMeta = (reviewStatus = {}, persistedValidation = {}) => {
  const traceMeta = reviewStatus.traceMeta || {};
  const persistedVerdict = persistedValidation.approval_status || null;
  return {
    ...traceMeta,
    program:
      reviewStatus.routing_program_hint
      || reviewStatus.program
      || traceMeta.program
      || null,
    runtimeArn:
      reviewStatus.agent_runtime_arn
      || traceMeta.runtimeArn
      || null,
    reviewState:
      reviewStatus.review_state
      || reviewStatus.reviewState
      || null,
    persistedVerdict,
    persistedVerdictSource: persistedVerdict
      ? 'chronos_validated_fundrequests.approval_status'
      : null,
  };
};

const ValidationTest = () => {
  const { getIdToken } = useAuth();
  const [frId, setFrId] = useState('');
  const [program, setProgram] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState([]);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // Agent-tweaking diagnostics: live run metadata (program/runtime, who
  // triggered it, retry count) and the agent execution trace pulled from
  // CloudWatch via GET /review-status/{id}?trace=true.
  const [runMeta, setRunMeta] = useState(null);
  const [trace, setTrace] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const mountedRef = useRef(true);
  // Tracks the FR id of the currently-active run so a late-resolving trace
  // fetch from a previous run can't overwrite the current run's state.
  const activeFrIdRef = useRef(null);

  // Fetch the agent execution trace for an FR. Best-effort: the backend
  // returns a `traceError` field (rather than failing) when logs can't be
  // read, so program teams always get *some* signal. The existing validation
  // detail endpoint contributes only its persisted approval_status to the ZIP.
  // Auto-invoked on timeout/failure; also available after completion.
  const fetchTrace = async (id) => {
    setTraceLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${getIdToken()}` };
      const resp = await fetch(`${VALIDATION_API}/review-status/${id}?trace=true`, {
        headers,
      });
      const data = await resp.json();

      let persistedValidation = {};
      try {
        const verdictResp = await fetch(`${VALIDATION_API}/validations/${id}`, {
          headers,
        });
        if (verdictResp.ok) persistedValidation = await verdictResp.json();
      } catch {
        // Best-effort diagnostic enrichment. Trace retrieval still succeeds if
        // no validated row exists yet or its independent read fails.
      }

      if (!mountedRef.current || activeFrIdRef.current !== id) return;
      setTrace({
        events: data.traceEvents || [],
        text: data.trace || '',
        meta: buildTraceMeta(data, persistedValidation),
        error: data.traceError || (resp.ok ? null : `HTTP ${resp.status}`),
      });
    } catch (e) {
      if (!mountedRef.current || activeFrIdRef.current !== id) return;
      setTrace({ events: [], text: '', meta: null, error: `Could not load trace: ${e.message}` });
    } finally {
      if (mountedRef.current && activeFrIdRef.current === id) setTraceLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // DROPDOWN: Populated from GET /programs API
  // Returns only programs the authenticated user has permission to execute.
  // Backend filters via resolve_permissions() → chronos_config role entries.
  // --------------------------------------------------------------------------
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    const token = getIdToken();
    fetch(`${VALIDATION_API}/programs`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (!mountedRef.current) return;
        if (data.programs && data.programs.length > 0) {
          setPrograms(data.programs.map(p => ({ label: p.label, value: p.value })));
        }
      })
      .catch(err => {
        // Surface the failure so an empty dropdown does not look identical to
        // "your account has no programs assigned" (which it did before this
        // fix — silent .catch hid 401/500 from the user).
        if (!mountedRef.current) return;
        console.error('[ValidationTest] Failed to load programs:', err);
        setError(`Failed to load programs: ${err.message}. Check your connection and refresh.`);
      });
  }, []);

  const handleSubmit = async () => {
    if (!frId.trim() || !program) return;

    const id = extractFrId(frId);
    const requestedProgram = program.value;

    // Popup is interactive-sized so SSO can complete inside it if the user
    // is not already logged into APFP. The previous 420x280 was unusable
    // for any redirect flow that surfaces UI (Cognito hosted login, IdP
    // consent screens). The mask-popup-on-top has been dropped for the
    // same reason — it hid any login prompt the user needed to act on.
    const w = 900, h = 700;
    const left = (window.screen.width - w) / 2;
    const top = (window.screen.height - h) / 2;
    // Prod APFP URL intentional: alpha-only feature captures the user's prod
    // token so the agent can validate prod fund requests from the alpha environment.
    const popup = window.open(
      `https://funding.apn.a2z.com/fund-requests/${id}`,
      'apfp_token_capture',
      `width=${w},height=${h},left=${left},top=${top}`
    );
    if (!popup) {
      setError('Popup blocked by browser. Please allow popups for this site and try again.');
      return;
    }

    setLoading(true);
    setProgress([{
      msg: `Target: ${id} · Agent: ${requestedProgram}`,
      status: 'in-progress',
      at: Date.now(),
    }]);
    setError(null);
    setResult(null);
    setRunMeta(null);
    setTrace(null);
    setTraceLoading(false);
    // Mark this id as the active run so stale trace fetches are ignored.
    activeFrIdRef.current = id;

    const token = getIdToken();

    try {
      // Wait for the userscript to send `chronos_apfp_token`. The userscript
      // also fires a `chronos_apfp_ready` ping on document-start; tracking
      // whether that ping arrived lets us tell "script not installed" apart
      // from "script installed but the user never logged in".
      //
      // We deliberately do NOT decide "script not installed" on a fixed 5s
      // timer. A not-logged-in user's popup redirects to Cognito (a different
      // origin), where the userscript can't run — no ping arrives until the
      // popup returns to funding.apn.a2z.com after login. Forcing a 5s
      // verdict would tell that user "install the script" while they were
      // mid-login, then close the popup. The popup-closed poll below catches
      // legitimate no-script cases (popup loads, no ping, user gives up and
      // closes it); the 30s deadline is the final arbiter.
      addProgress('Waiting for APFP token capture...', 'in-progress');

      let scriptReady = false;
      let apfpToken = null;
      const TOKEN_TIMEOUT_MS = 30000;

      const result = await new Promise((resolve) => {
        let resolved = false;
        const finish = (outcome) => {
          if (resolved) return;
          resolved = true;
          window.removeEventListener('message', handler);
          clearInterval(closedCheck);
          clearTimeout(timeoutId);
          resolve(outcome);
        };
        const handler = (event) => {
          if (event.origin !== 'https://funding.apn.a2z.com') return;
          const data = event.data;
          if (!data || typeof data !== 'object') return;
          if (data.type === 'chronos_apfp_ready') {
            scriptReady = true;
            return;
          }
          if (data.type === 'chronos_apfp_token' && data.token) {
            finish({ outcome: 'token', token: data.token });
          }
        };
        window.addEventListener('message', handler);

        // If the user closes the popup before a token arrives, decide based
        // on whether the userscript ever pinged. Polling because there is no
        // cross-origin event for popup close.
        const closedCheck = setInterval(() => {
          if (popup.closed) {
            finish({ outcome: scriptReady ? 'no_token' : 'no_script' });
          }
        }, 500);

        // Hard deadline — covers the case where the popup stays open but
        // nothing ever happens (e.g. APFP hung, browser blocked the script,
        // user walked away). Final outcome still keys off the readiness ping.
        const timeoutId = setTimeout(() => {
          finish({ outcome: scriptReady ? 'no_token' : 'no_script' });
        }, TOKEN_TIMEOUT_MS);
      });

      if (popup && !popup.closed) popup.close();

      if (result.outcome === 'no_script') {
        addProgress(
          '✗ No token captured. Check all three: (1) the capture userscript is installed from the link above, (2) Tampermonkey\'s "Allow User Scripts" toggle is ON (current Chrome requires it, or the script installs but never runs), and (3) you are logged into prod APFP.',
          'error'
        );
        setLoading(false);
        return;
      }
      if (result.outcome === 'no_token') {
        addProgress(
          '✗ Script is installed but no token was captured. Check: (1) you are logged into prod APFP — complete the login in the popup if not — and (2) popups are allowed for alpha.chronos.aws.dev.',
          'error'
        );
        setLoading(false);
        return;
      }
      apfpToken = result.token;
      addProgress('✓ Token captured', 'success');

      // Submit validation with token
      addProgress('Submitting for validation...', 'in-progress');

      // ----------------------------------------------------------------------
      // SUBMIT: POST /validate triggers the agent with token in payload.
      // invoke_validation writes "1-Pending" to DDB, then if apfp_token is
      // present, invokes the agent directly (bypasses stream_processor).
      // ----------------------------------------------------------------------
      const resp = await fetch(`${VALIDATION_API}/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(buildValidationRequest(id, requestedProgram, apfpToken)),
      });
      if (resp.ok) {
        const data = await resp.json();
        addProgress(`✓ ${data.message || "Submitted"}`, "success");
      } else if (resp.status === 504 || resp.status === 502) {
        // Gateway timeout — agent likely still running behind the scenes
        addProgress('✓ Validation submitted (polling for result...)', 'success');
      } else {
        const errBody = await resp.text().catch(() => '');
        addProgress(`✗ Submission failed: HTTP ${resp.status} ${errBody}`, 'error');
        setLoading(false);
        return;
      }
      addProgress('Waiting for agent to complete...', 'in-progress');

      // ----------------------------------------------------------------------
      // POLLING: GET /review-status/{id} — shows state transitions from API
      // States: 1-Pending → 2-In Progress → 3-Completed / 4-Failed
      // Each transition is displayed as a progress step to the user
      // ----------------------------------------------------------------------
      const startTime = Date.now();
      let lastState = '';
      const poll = async () => {
        // Stale poll from a previous run — a newer run has taken over. Bail
        // before touching shared progress/result/runMeta state.
        if (activeFrIdRef.current !== id) return;
        if (Date.now() - startTime > POLL_TIMEOUT) {
          addProgress('✗ Timed out waiting for agent', 'error');
          addProgress('Fetching agent trace to show where it stalled...', 'in-progress');
          fetchTrace(id);
          setLoading(false);
          return;
        }

        try {
          const statusResp = await fetch(`${VALIDATION_API}/review-status/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const statusData = await statusResp.json();
          // A newer run started while this fetch was in flight — don't let a
          // stale poll write into the active run's state (runMeta/result/etc).
          if (!mountedRef.current || activeFrIdRef.current !== id) return;
          const state = statusData.review_state || statusData.reviewState;
          const effectiveProgram = getEffectiveProgram(statusData, requestedProgram);

          // Capture run metadata for the Progress panel — shows program teams
          // which program/runtime ran, who triggered it, and the retry count.
          if (mountedRef.current) {
            setRunMeta({
              program: effectiveProgram,
              requestedProgram,
              triggeredBy: statusData.triggered_by || '-',
              retryCount: statusData.retry_count ?? '-',
              lastUpdated: statusData.last_updated || null,
              state,
            });
          }

          // Fail visibly if an old or incorrect backend ignores the selector.
          if (state && effectiveProgram !== requestedProgram) {
            addProgress(
              `✗ Requested ${requestedProgram}, but the backend routed ${effectiveProgram}`,
              'error'
            );
            fetchTrace(id);
            setLoading(false);
            return;
          }

          if (state && state !== lastState) {
            lastState = state;
            if (state === '1-Pending') addProgress('Agent triggered — waiting for stream processor...', 'in-progress');
            if (state === '2-In Progress') addProgress('Agent is processing — running validation tools...', 'in-progress');
          }

          if (state === '3-Completed') {
            addProgress('✓ Agent completed', 'success');
            // Fetch full result
            const resultResp = await fetch(`${VALIDATION_API}/validations/${id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const resultData = await resultResp.json();
            // Second await in this branch — re-check the run is still active
            // before writing its result/trace into shared state.
            if (!mountedRef.current || activeFrIdRef.current !== id) return;
            setResult({
              fr_id: id,
              program: effectiveProgram,
              approval_summary: resultData.approval_summary || resultData,
              // GET /validations/{id} returns the DynamoDB item, where every
              // program nests this block under validation_data — the top-level
              // key only exists in the S3 results file MAP writes separately.
              // Reading top-level alone made the Partner/Customer fallbacks
              // below dead code for all programs.
              request_data_summary:
                resultData.request_data_summary
                || (resultData.validation_data || {}).request_data_summary
                || {},
              // Raw APFP payload (curated + redacted by the agent) for the
              // "Raw APFP" panel + export. Program-agnostic: any program that
              // persists validation_data.complete_api_data gets it; absent =>
              // muted note, not an error.
              complete_api_data:
                (resultData.validation_data || {}).complete_api_data || null,
            });
            addProgress('Done.', 'success');
            // Pull the trace too so program teams can inspect tool-by-tool
            // execution even on a clean pass (tuning thresholds, timing, etc).
            fetchTrace(id);
            setLoading(false);
            return;
          } else if (state === '4-Failed') {
            addProgress('✗ Agent failed', 'error');
            setResult({
              fr_id: id,
              program: effectiveProgram,
              approval_summary: { decision: 'AGENT_FAILED', key_findings: ['Agent returned failed state'], required_actions: [] },
            });
            fetchTrace(id);
            setLoading(false);
            return;
          }

          // Still processing
          if (mountedRef.current && activeFrIdRef.current === id) setTimeout(poll, POLL_INTERVAL);
        } catch (pollErr) {
          if (activeFrIdRef.current !== id) return;
          addProgress(`✗ Poll error: ${pollErr.message}`, 'error');
          setLoading(false);
        }
      };
      poll();
      return;
    } catch (err) {
      setError(err.message);
      addProgress(`✗ Error: ${err.message}`, 'error');
    }
    setLoading(false);
  };

  // --------------------------------------------------------------------------
  // EXPORT: Generates ZIP with 4 files
  //   - decision.json: full structured result from the agent
  //   - summary.md: human-readable markdown report
  //   - report.html: styled visual report (Chronos design, opens in browser)
  //   - cloudwatch_trace.log: agent execution trace (best-effort)
  // --------------------------------------------------------------------------
  const handleExport = async () => {
    if (!result) return;
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const exportFrId = result.fr_id || 'unknown';
    const exportProgram = result.program || '-';
    const summary = result.approval_summary || {};
    const requestData = result.request_data_summary || {};
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

    zip.file('decision.json', JSON.stringify(result, null, 2));

    // Raw APFP payload (curated + redacted) so the export is self-contained
    // for "parser vs data" diagnosis. Omitted when the run didn't capture it.
    if (result.complete_api_data) {
      zip.file('raw_apfp.json', JSON.stringify(result.complete_api_data, null, 2));
    }

    const md = [
      `# Validation Report`,
      ``,
      `| Field | Value |`,
      `|-------|-------|`,
      `| **FR ID** | \`${exportFrId}\` |`,
      `| **Program** | ${exportProgram} |`,
      `| **Partner** | ${summary.partner || requestData.partner_name || "-"} |`,
      `| **Decision** | ${summary.decision || "-"} |`,
      ...(summary.needs_review_reason
        ? [`| **Needs-review reason** | ${formatNeedsReviewReason(summary.needs_review_reason)} |`]
        : []),
      ...(summary.needs_review_detail
        ? [`| **Why this needs review** | ${escapeMarkdownCell(summary.needs_review_detail)} |`]
        : []),
      ...(summary.reject_detail
        ? [`| **Why this was rejected** | ${escapeMarkdownCell(summary.reject_detail)} |`]
        : []),
      ...(summary.line_items_translated_from
        ? [`| **Line items translated from** | ${escapeMarkdownCell(summary.line_items_translated_from)} |`]
        : []),
      ``,
      `## Key Findings`,
      ``,
      ...(summary.key_findings || []).map(f => `- ${f}`),
      ``,
      `## Required Actions`,
      ``,
      ...(summary.required_actions || ['No actions required']).map(a => `- ${a}`),
    ].join('\n');
    zip.file('summary.md', md);

    // report.html — visual report
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Validation Report - ${escapeHtml(exportFrId)}</title>
<style>
*{box-sizing:border-box}body{font-family:'Amazon Ember','Helvetica Neue',Arial,sans-serif;max-width:860px;margin:0 auto;padding:24px;color:#16191f;font-size:14px;line-height:1.5;background:#f2f3f3}
.card{background:#fff;border:1px solid #d5dbdb;border-radius:8px;padding:20px;margin-bottom:16px}
.card-header{font-size:16px;font-weight:700;margin-bottom:12px;padding-bottom:8px;border-bottom:1px solid #eaeded}
h1{font-size:20px;font-weight:700;margin:0 0 16px}
.badge{display:inline-block;padding:2px 8px;border-radius:4px;font-weight:600;font-size:11px;text-transform:uppercase}
.badge-approve{background:#d1f0d1;color:#037f0c}.badge-reject{background:#fce9e9;color:#d13212}.badge-review{background:#fef3cd;color:#8a6d00}
.kv-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin:8px 0}
.kv-item .label{font-size:11px;font-weight:600;color:#545b64;text-transform:uppercase;margin-bottom:2px}
.kv-item .value{font-size:13px}
ul{padding-left:16px;margin:8px 0}li{margin-bottom:2px;font-size:13px}
.footer{margin-top:24px;padding-top:12px;border-top:1px solid #eaeded;color:#687078;font-size:11px}
</style></head><body>
<div class="card">
<h1>Program Validation Report</h1>
<span class="badge badge-${(summary.decision||'').toLowerCase().includes('approve')?'approve':(summary.decision||'').toLowerCase().includes('reject')?'reject':'review'}">${escapeHtml(summary.decision || '-')}</span>
${summary.needs_review_reason ? `<span style="margin-left:8px;font-size:12px;color:#8a6d00">Reason: ${escapeHtml(formatNeedsReviewReason(summary.needs_review_reason))}</span>` : ''}
${summary.needs_review_detail ? `<div style="margin-top:10px;padding:10px 12px;background:#fef3cd;border-left:3px solid #d9a400;border-radius:4px;font-size:13px;color:#4d3f00"><strong>Why this needs review:</strong> ${escapeHtml(summary.needs_review_detail)}</div>` : ''}
${summary.reject_detail ? `<div style="margin-top:10px;padding:10px 12px;background:#fce9e9;border-left:3px solid #d13212;border-radius:4px;font-size:13px;color:#5c1f14"><strong>Why this was rejected:</strong> ${escapeHtml(summary.reject_detail)}</div>` : ''}
${summary.line_items_translated_from ? `<div style="margin-top:8px;font-size:12px;color:#687078"><em>Line items machine-translated from ${escapeHtml(summary.line_items_translated_from)} to English by Chronos — spot-check advised.</em></div>` : ''}
<div class="kv-grid">
<div class="kv-item"><div class="label">FR ID</div><div class="value">${escapeHtml(exportFrId)}</div></div>
<div class="kv-item"><div class="label">Program Name</div><div class="value">${escapeHtml(exportProgram || '-')}</div></div>
<div class="kv-item"><div class="label">Partner</div><div class="value">${escapeHtml(summary.partner || requestData.partner_name || '-')}</div></div>
<div class="kv-item"><div class="label">Customer</div><div class="value">${escapeHtml(summary.customer || requestData.customer_company_name || '-')}</div></div>
<div class="kv-item"><div class="label">Total Funds Requested</div><div class="value">${escapeHtml(summary.total_fund_requested || '-')}</div></div>
<div class="kv-item"><div class="label">Phase / Sub-Program</div><div class="value">${escapeHtml(summary.migration_phase || requestData.sub_program || '-')}</div></div>
<div class="kv-item"><div class="label">Validation Summary</div><div class="value">${escapeHtml(summary.validation_summary || '-')}</div></div>
<div class="kv-item"><div class="label">Recommendation</div><div class="value">${escapeHtml(summary.recommendation || '-')}</div></div>
</div></div>
<div class="card"><div class="card-header">Key Findings</div>
<ul>${(summary.key_findings || []).map(f => `<li>${escapeHtml(f)}</li>`).join('')}</ul></div>
${(summary.required_actions || []).length > 0 ? `<div class="card"><div class="card-header">Required Actions</div>
<ul>${summary.required_actions.map(a => `<li>⚡ ${escapeHtml(a)}</li>`).join('')}</ul></div>` : ''}
<div class="footer">Generated by Chronos Program Validation — ${new Date().toISOString().slice(0,19)} UTC</div>
</body></html>`;
    zip.file('report.html', html);

    // cloudwatch_trace.log — best effort. Reuse the trace already fetched
    // during the run if present; otherwise fetch with ?trace=true. Without
    // the query param the backend omits the trace field entirely, so the
    // param is required for this file to have content.
    try {
      let traceText = trace?.text || '';
      if (!traceText) {
        const traceResp = await fetch(`${VALIDATION_API}/review-status/${exportFrId}?trace=true`, {
          headers: { 'Authorization': `Bearer ${getIdToken()}` }
        });
        const traceData = await traceResp.json();
        traceText = traceData.trace || '';
      }
      if (traceText) zip.file('cloudwatch_trace.log', traceText);
    } catch (e) { /* best effort */ }

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportFrId}_${timestamp}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --------------------------------------------------------------------------
  // EXPORT TRACE: lighter-weight ZIP scoped to the agent execution trace —
  // for handing off a stuck/failed run to a program team without the full
  // result bundle. Works for any outcome (success, 4-Failed, timeout) and
  // even when trace.error is set (we still ship the diagnosis).
  // --------------------------------------------------------------------------
  const handleExportTrace = async () => {
    if (!trace) return;
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const exportFrId = activeFrIdRef.current || 'unknown';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const localDiag = summarizeTrace(trace.events, runMeta?.state);

    zip.file('agent_trace.log',
      trace.text || '(no joined trace text — see trace_events.json for structured events)');
    zip.file('trace_events.json', JSON.stringify(trace.events || [], null, 2));
    zip.file('trace_diagnosis.json', JSON.stringify({
      fr_id: exportFrId,
      requested_program: runMeta?.requestedProgram || program?.value || '-',
      program: trace.meta?.program || runMeta?.program || '-',
      review_state: trace.meta?.reviewState || runMeta?.state || '-',
      runtime_arn: trace.meta?.runtimeArn || null,
      persisted_verdict: trace.meta?.persistedVerdict || null,
      verdict: localDiag?.verdict || null,
      agent_completed: localDiag?.agentCompleted ?? null,
      errors: localDiag?.errs || [],
      meta: trace.meta || null,
      trace_error: trace.error || null,
    }, null, 2));

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportFrId}_agent-trace_${timestamp}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const addProgress = (msg, status) => {
    setProgress(prev => [...prev, { msg, status, at: Date.now() }]);
  };

  // Render-time derived values.
  const runStartAt = progress.length ? progress[0].at : null;
  const hasError = progress.some((p) => p.status === 'error');
  // Trace verdict + errors are derived once per render from the same `trace`
  // state used by the existing event list, so they always agree with what's
  // shown below them. Pure helper — see summarizeTrace at module scope.
  const diag = trace ? summarizeTrace(trace.events, runMeta?.state) : null;

  return (
    <SpaceBetween size="l">
      <Alert type="info" header="One-time setup: install token capture script">
        Validation captures your APFP token via a Tampermonkey userscript hosted
        on this site. Install or update it with one click —{' '}
        <a
          href="/chronos-token-capture.user.js"
          target="_blank"
          rel="noreferrer"
        >
          chronos-token-capture.user.js
        </a>
        . Tampermonkey will offer to install it; subsequent updates are picked
        up automatically. Requires the{' '}
        <a
          href="https://chrome.google.com/webstore/detail/tampermonkey/dhdgffkkebhmkfjojejmpbldmpobfkfo"
          target="_blank"
          rel="noreferrer"
        >
          Tampermonkey extension
        </a>
        {' '}with <strong>"Allow User Scripts"</strong> enabled (current Chrome
        installs the script but won't run it until that toggle is on).
      </Alert>
      <Container header={<Header variant="h2">Program Validation</Header>}>
        <SpaceBetween size="m">
          <FormField
            label="Agent to run"
            description="The alpha validation test routes to this selected agent."
          >
            <Select
              selectedOption={program}
              onChange={({ detail }) => setProgram(detail.selectedOption)}
              options={programs}
              placeholder="Select agent"
              disabled={loading}
            />
          </FormField>
          <FormField label="Fund Request ID">
            <Input
              value={frId}
              onChange={({ detail }) => setFrId(detail.value)}
              placeholder="FR ID or full APFP URL (e.g. https://funding.apn.a2z.com/fund-requests/FR-...)"
              disabled={loading}
            />
          </FormField>
          <Button variant="primary" onClick={handleSubmit} loading={loading} disabled={!frId.trim() || !program}>
            Validate FR
          </Button>
        </SpaceBetween>
      </Container>

      {progress.length > 0 && (
        <Container header={<Header variant="h3">Progress</Header>}>
          <SpaceBetween size="xs">
            {runMeta && (
              <Box color="text-body-secondary" fontSize="body-s">
                <strong>Agent:</strong> {runMeta.program}
                {'  ·  '}<strong>Triggered by:</strong> {runMeta.triggeredBy}
                {'  ·  '}<strong>Retries:</strong> {String(runMeta.retryCount)}
                {runMeta.state ? <>{'  ·  '}<strong>State:</strong> {runMeta.state}</> : null}
              </Box>
            )}
            {progress.map((p, i) => {
              const elapsed = runStartAt != null ? (p.at - runStartAt) / 1000 : null;
              return (
                <Box key={i}>
                  <StatusIndicator type={p.status === 'error' ? 'error' : p.status === 'success' ? 'success' : 'in-progress'}>
                    {p.msg}
                  </StatusIndicator>
                  {elapsed != null && elapsed > 0 && (
                    <Box variant="span" color="text-body-secondary" fontSize="body-s">
                      {`  (+${elapsed.toFixed(1)}s)`}
                    </Box>
                  )}
                </Box>
              );
            })}
          </SpaceBetween>
        </Container>
      )}

      {(traceLoading || trace) && (
        <Container header={<Header variant="h3">Agent execution trace</Header>}>
          <ExpandableSection
            headerText="Tool-by-tool execution, errors and timing (from CloudWatch)"
            defaultExpanded={hasError}
          >
            <SpaceBetween size="xs">
              {traceLoading && <StatusIndicator type="loading">Loading agent trace…</StatusIndicator>}
              {trace?.error && (
                <Alert type="warning" header="Agent trace unavailable">
                  {trace.error}
                </Alert>
              )}
              {/*
                Gate the verdict on having events to reason about. With zero
                events the warning's "may still be running, have crashed, or
                never started" implies we observed evidence — but we didn't.
                The empty-state message below is the accurate one in that
                case. The error verdict already implicitly requires events
                (needs an "Invocation completed" line), so the gate doesn't
                affect that branch.
              */}
              {trace && !trace.error && diag?.verdict && (trace.events?.length ?? 0) > 0 && (
                <Alert type={diag.verdictType} header="Diagnosis">
                  {diag.verdict}
                </Alert>
              )}
              {trace && !trace.error && diag?.errs?.length > 0 && (
                <Box>
                  <Box variant="h4" padding={{ bottom: 'xxs' }}>Errors</Box>
                  <SpaceBetween size="xxs">
                    {diag.errs.map((e, i) => (
                      <Box key={`err-${i}`} fontSize="body-s">
                        <Box variant="span" color="text-body-secondary" fontSize="body-s">
                          {fmtClock(e.timestamp)}{' '}
                        </Box>
                        <StatusIndicator type="error">
                          {`「${String(e.level || 'ERROR').toUpperCase()}」 ${e.message || ''}`}
                        </StatusIndicator>
                        {(e.error_type || e.error_message) && (
                          <Box variant="span" color="text-status-error" fontSize="body-s">
                            {`  — ${e.error_type || ''}${e.error_type && e.error_message ? ': ' : ''}${e.error_message || ''}`}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </SpaceBetween>
                </Box>
              )}
              {trace?.meta?.logGroup && (
                <Box color="text-body-secondary" fontSize="body-s">
                  {`Runtime log group: ${trace.meta.logGroup}  ·  ${trace.meta.eventCount} events`}
                  {trace.meta.lookbackMinutes ? `  ·  last ${trace.meta.lookbackMinutes}m` : ''}
                  {trace.meta.truncated ? '  ·  (truncated — showing most recent)' : ''}
                </Box>
              )}
              {!traceLoading && trace && !trace.error && (trace.events?.length ?? 0) === 0 && (
                <Box color="text-body-secondary">
                  No matching agent log lines found for this fund request in the lookback window.
                </Box>
              )}
              {trace && (
                <Button iconName="download" onClick={handleExportTrace}>Export Trace (ZIP)</Button>
              )}
              {trace?.events?.map((e, i) => (
                <Box key={i} fontSize="body-s">
                  <Box variant="span" color="text-body-secondary" fontSize="body-s">
                    {fmtClock(e.timestamp)}{' '}
                  </Box>
                  <StatusIndicator type={traceLevelToType(e.level)}>
                    {e.message || e.level}
                  </StatusIndicator>
                  {(e.error_type || e.error_message) && (
                    <Box variant="span" color="text-status-error" fontSize="body-s">
                      {`  — ${e.error_type || ''}${e.error_type && e.error_message ? ': ' : ''}${e.error_message || ''}`}
                    </Box>
                  )}
                </Box>
              ))}
            </SpaceBetween>
          </ExpandableSection>
        </Container>
      )}

      {error && <Alert type="error">{error}</Alert>}

      {result && (
        <Container header={<Header variant="h3">Result</Header>}>
          <SpaceBetween size="m">
            <ColumnLayout columns={3}>
              <Box><strong>FR ID:</strong> {result.fr_id}</Box>
              <Box><strong>Decision:</strong> <Badge color={getDecisionColor(result.approval_summary?.decision)}>{result.approval_summary?.decision || '-'}</Badge></Box>
              <Box><strong>Program:</strong> {result.program}</Box>
            </ColumnLayout>
            {result.approval_summary?.needs_review_reason && (
              <Box><strong>Reason:</strong> {formatNeedsReviewReason(result.approval_summary.needs_review_reason)}</Box>
            )}
            {result.approval_summary?.needs_review_detail && (
              <Box><strong>Why this needs review:</strong> {result.approval_summary.needs_review_detail}</Box>
            )}
            {result.approval_summary?.reject_detail && (
              <Box><strong>Why this was rejected:</strong> {result.approval_summary.reject_detail}</Box>
            )}
            {result.approval_summary?.line_items_translated_from && (
              <Box color="text-body-secondary">
                <em>Line items machine-translated from {result.approval_summary.line_items_translated_from} to English by Chronos — spot-check advised.</em>
              </Box>
            )}
            <ColumnLayout columns={3}>
              {/* Last resort for Partner is the curated APFP envelope, which
                  carries fundRequest.partner.name for every program. Customer
                  has no such fallback on purpose: apfp_curation drops
                  customerCompanyName as PII, so a program that wants it shown
                  must promote it in its own parser (as Rubicon does). */}
              <Box><strong>Partner:</strong> {result.approval_summary?.partner || result.request_data_summary?.partner_name || result.complete_api_data?.fundRequest?.partner?.name || '-'}</Box>
              <Box><strong>Customer:</strong> {result.approval_summary?.customer || result.request_data_summary?.customer_company_name || '-'}</Box>
              <Box><strong>Total Funds Requested:</strong> {result.approval_summary?.total_fund_requested || '-'}</Box>
            </ColumnLayout>
            <ColumnLayout columns={3}>
              <Box><strong>Phase / Sub-Program:</strong> {result.approval_summary?.migration_phase || result.request_data_summary?.sub_program || '-'}</Box>
              <Box><strong>Validation Summary:</strong> {result.approval_summary?.validation_summary || '-'}</Box>
            </ColumnLayout>
            {result.approval_summary?.key_findings?.length > 0 && (
              <Box>
                <Box variant="h4" padding={{ bottom: 'xxs' }}>Validation checks</Box>
                <SpaceBetween size="xxs">
                  {result.approval_summary.key_findings.map((f, i) => {
                    const c = parseKeyFinding(f);
                    return (
                      <Box key={i} fontSize="body-s">
                        <StatusIndicator type={c.status}>
                          <strong>{c.name}</strong>
                        </StatusIndicator>
                        {c.reason && (
                          <Box variant="span" color="text-body-secondary" fontSize="body-s">
                            {`  — ${c.reason}`}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </SpaceBetween>
              </Box>
            )}
            {result.approval_summary?.required_actions?.length > 0 && (
              <Box>
                <strong>Required Actions:</strong>
                <ul>{result.approval_summary.required_actions.map((a, i) => <li key={i}>{a}</li>)}</ul>
              </Box>
            )}
            {/* Raw APFP payload — program-agnostic. Shows what APFP sent the
                agent (curated + PII-redacted server-side) so a team can tell a
                parser bug from missing data. */}
            <ExpandableSection headerText="Raw APFP payload">
              {result.complete_api_data ? (
                <Box variant="code" fontSize="body-s">
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(result.complete_api_data, null, 2)}
                  </pre>
                </Box>
              ) : (
                <Box color="text-body-secondary" fontSize="body-s">
                  No raw payload captured for this run. (The agent persists
                  validation_data.complete_api_data; older runs or programs not
                  yet on the shared curator won't have it.)
                </Box>
              )}
            </ExpandableSection>
            <Button iconName="download" onClick={handleExport}>Export Results (ZIP)</Button>
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};

export default ValidationTest;
